import pathMapper from "./pathMapper";

export const PUSH_ICON_PATH = "/images/app-icon-v2.png";
const DEFAULT_PUSH_PATH = "/praxisplay/role-play";

export type FcmPayload = {
  notification?: {
    title?: string;
    body?: string;
  };
  data?: Record<string, string>;
};

export function getPushNotificationContent(payload: FcmPayload) {
  const data = payload?.data || {};
  const title =
    payload?.notification?.title || data.title || "Notification";
  const body = payload?.notification?.body || data.body || data.text || "";
  return { title, body, data };
}

/** Flatten FCM's notification.data envelope so path / session_id are top-level. */
export function unwrapPushNotificationData(
  raw?: Record<string, unknown> | null
): Record<string, string> {
  if (!raw) {
    return {};
  }
  const fcmMsg = raw.FCM_MSG;
  if (fcmMsg && typeof fcmMsg === "object") {
    const nested = (fcmMsg as { data?: Record<string, string> }).data;
    return nested || {};
  }
  return raw as Record<string, string>;
}

export function getPushNotificationPath(
  data?: Record<string, string> | Record<string, unknown> | null
) {
  const fields = unwrapPushNotificationData(data as Record<string, unknown> | null);
  if (fields.path === "praxisplay-feedback" && fields.session_id) {
    return `/praxisplay/session/${fields.session_id}`;
  }
  if (fields.path && fields.path in pathMapper) {
    return pathMapper[fields.path as keyof typeof pathMapper];
  }
  return DEFAULT_PUSH_PATH;
}

export async function showForegroundPushNotification(
  payload: FcmPayload
): Promise<void> {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (window.Notification.permission !== "granted") return;

  const { title, body, data } = getPushNotificationContent(payload);
  if (!body && title === "Notification") return;

  const options: NotificationOptions = {
    body,
    icon: PUSH_ICON_PATH,
    badge: PUSH_ICON_PATH,
    data,
  };

  try {
    // Page-level Notification is what Chrome puts in Notification Center while
    // the tab is open. SW showNotification is often suppressed for a focused tab.
    const native = new window.Notification(title, {
      body,
      icon: PUSH_ICON_PATH,
      tag: data.session_id || data.path || "praxis-push",
      data,
    });
    native.onclick = () => {
      window.focus();
      const path = getPushNotificationPath(data);
      if (path && path !== "/") {
        window.location.assign(path);
      }
      native.close();
    };
  } catch {
    const registration = await navigator.serviceWorker?.ready;
    if (registration?.showNotification) {
      await registration.showNotification(title, options);
    }
  }
}
